import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Trash2 } from "lucide-react";
import api from "@/lib/api";

// Minimal markdown: **bold**, "- " bullets, blank-line paragraphs.
function Md({ text }) {
  const renderInline = (s) =>
    s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  return (
    <div className="space-y-1">
      {(text || "").split("\n").map((line, i) =>
        line.trim() === "" ? <div key={i} className="h-1" /> :
        line.startsWith("- ")
          ? <div key={i} className="flex gap-1.5 pl-1"><span style={{ color: "var(--hoip-text-tertiary)" }}>•</span><span>{renderInline(line.slice(2))}</span></div>
          : <div key={i}>{renderInline(line)}</div>
      )}
    </div>
  );
}

function AssistantMessage({ m, navigate, onClose }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3 py-2 rounded-sm text-[14px] leading-relaxed"
          style={{ background: "rgba(59,130,246,0.14)", color: "var(--hoip-text-primary)" }}
          data-testid="assistant-msg-user">{m.content}</div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[95%] px-3 py-2.5 rounded-sm text-[14px] leading-relaxed border"
        style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
        data-testid="assistant-msg-assistant">
        <Md text={m.content} />
        {(m.links || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {m.links.map((l) => (
              <button key={l.path + l.label} onClick={() => { navigate(l.path); onClose(); }}
                className="text-[12px] px-2 py-0.5 rounded-sm border hover:bg-[var(--hoip-hover)] transition-colors"
                style={{ borderColor: "var(--hoip-border)", color: "#3B82F6" }}
                data-testid="assistant-deeplink">→ {l.label}</button>
            ))}
          </div>
        )}
        {m.disclaimer && (
          <div className="mt-2 pt-1.5 border-t text-[11px] italic leading-snug"
            style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}
            data-testid="assistant-disclaimer">{m.disclaimer}</div>
        )}
        {m.provenance && (
          <div className="mt-1 text-[11px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="assistant-provenance">{m.provenance}</div>
        )}
      </div>
    </div>
  );
}

export const PortfolioAssistant = () => {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [banner, setBanner] = useState(false);
  const [open, setOpen] = useState(() => sessionStorage.getItem("assistant_open") === "1");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("assistant_session") || crypto.randomUUID());
  const bottomRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("assistant_session", sessionRef.current);
    const locallyDismissed = localStorage.getItem("assistant_banner_dismissed") === "1";
    api.get("/assistant/access").then(({ data }) => {
      setEnabled(!!data.enabled);
      setBanner(!!data.enabled && !data.banner_dismissed && !locallyDismissed);
    }).catch(() => setEnabled(false));
  }, []);

  const setOpenPersist = useCallback((v) => {
    setOpen(v);
    sessionStorage.setItem("assistant_open", v ? "1" : "0");
  }, []);

  const dismissBanner = useCallback((openPanel) => {
    setBanner(false);
    if (openPanel) setOpenPersist(true);
    localStorage.setItem("assistant_banner_dismissed", "1");
    api.post("/assistant/banner/dismiss").catch(() => {});
  }, [setOpenPersist]);

  useEffect(() => {
    if (!open || !enabled) return;
    api.get("/assistant/history", { params: { session_id: sessionRef.current } })
      .then(({ data }) => setMessages((cur) => (cur.length ? cur : (data.messages || [])))).catch(() => {});
  }, [open, enabled]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/message", { message: text, session_id: sessionRef.current });
      setMessages((m) => [...m, { role: "assistant", content: data.body, links: data.links, disclaimer: data.disclaimer, provenance: data.provenance }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Something went wrong reaching the assistant service — no data was computed.", links: [] }]);
    } finally {
      setBusy(false);
    }
  }, [input, busy]);

  const clearHistory = async () => {
    try { await api.delete("/assistant/history"); setMessages([]); } catch (e) { /* noop */ }
  };

  if (!enabled) return null;
  return (
    <>
      {banner && !open && (
        <div className="fixed bottom-16 right-4 z-50 w-[300px] max-w-[calc(100vw-2rem)] border rounded-sm shadow-2xl p-3"
          style={{ background: "var(--hoip-card)", borderColor: "rgba(59,130,246,0.45)" }}
          data-testid="assistant-announcement-banner">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4 shrink-0" style={{ color: "#3B82F6" }} />
              <span className="text-[12px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>Meet the Portfolio Assistant</span>
              <span className="text-[8px] px-1 rounded-sm font-bold" style={{ background: "rgba(59,130,246,0.14)", color: "#3B82F6" }}>BETA</span>
            </div>
            <button onClick={() => dismissBanner(false)} className="p-0.5 rounded-sm hover:bg-[var(--hoip-hover)] shrink-0"
              data-testid="assistant-banner-dismiss-btn">
              <X className="w-3.5 h-3.5" style={{ color: "var(--hoip-text-tertiary)" }} />
            </button>
          </div>
          <div className="mt-1.5 text-[10px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
            Ask questions like <em>"Is my downside protected?"</em> and get plain-language readouts of what your own
            engines and Settings already compute. No recommendations — just your numbers, explained.
          </div>
          <button onClick={() => dismissBanner(true)}
            className="mt-2 w-full text-[11px] font-bold py-1.5 rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "rgba(59,130,246,0.45)", color: "#3B82F6" }}
            data-testid="assistant-banner-try-btn">Try it →</button>
        </div>
      )}
      <button onClick={() => setOpenPersist(true)}
          className={`fixed bottom-4 z-40 flex items-center gap-1.5 px-3 py-2 rounded-full border shadow-lg ${open ? "right-auto left-4 md:right-[440px] md:left-auto" : "right-4"}`}
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
          data-testid="assistant-open-btn">
          <MessageCircle className="w-4 h-4" style={{ color: "#3B82F6" }} />
          <span className="text-[12px] font-bold">Portfolio Assistant</span>
          <span className="text-[8px] px-1 rounded-sm font-bold" style={{ background: "rgba(59,130,246,0.14)", color: "#3B82F6" }}>BETA</span>
        </button>
      {open && (
        <div className="fixed z-50 flex flex-col border shadow-2xl
            inset-x-0 bottom-0 h-[90vh] w-full
            md:inset-auto md:top-0 md:right-0 md:h-screen md:w-[420px]"
          style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)" }}
          data-testid="assistant-panel">
          <div className="px-3 py-2 border-b flex items-center justify-between shrink-0" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <div>
              <div className="text-[15px] font-bold flex items-center gap-1.5" style={{ color: "var(--hoip-text-primary)" }}>
                Portfolio Assistant
                <span className="text-[8px] px-1 rounded-sm font-bold" style={{ background: "rgba(59,130,246,0.14)", color: "#3B82F6" }}>BETA</span>
              </div>
              <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Natural-language readouts of your own analytics</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={clearHistory} title="Delete conversation history" className="p-1 rounded-sm hover:bg-[var(--hoip-hover)]" data-testid="assistant-clear-btn">
                <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--hoip-text-tertiary)" }} />
              </button>
              <button onClick={() => setOpenPersist(false)} className="p-1 rounded-sm hover:bg-[var(--hoip-hover)]" data-testid="assistant-close-btn">
                <X className="w-4 h-4" style={{ color: "var(--hoip-text-tertiary)" }} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2.5 space-y-2" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-[13px] leading-relaxed px-1 py-2" style={{ color: "var(--hoip-text-tertiary)" }}>
                Educational readouts from cached MarkovLab data — quotes, structures, Donchian levels, and disclosure activity.
                Not personalized advice. Example: <em>"What structures compare for a 35–45 DTE premium on TSLA?"</em>
              </div>
            )}
            {messages.map((m, i) => <AssistantMessage key={i} m={m} navigate={navigate} onClose={() => setOpenPersist(false)} />)}
            {busy && (
              <div className="text-[10px] px-1 animate-pulse" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="assistant-thinking">
                Computing readout from your engines…
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-2 border-t flex items-center gap-1.5 shrink-0" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask about your downside protection or a stock quote…"
              className="flex-1 bg-transparent border rounded-sm px-2 py-2 text-[14px] outline-none focus:border-blue-500"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
              data-testid="assistant-input" />
            <button onClick={send} disabled={busy || !input.trim()}
              className="p-1.5 rounded-sm border disabled:opacity-40 hover:bg-[var(--hoip-hover)] transition-colors"
              style={{ borderColor: "var(--hoip-border)" }} data-testid="assistant-send-btn">
              <Send className="w-3.5 h-3.5" style={{ color: "#3B82F6" }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
